/**
 * What a post-redirect arrival should report.
 *
 * Sign-up and sign-in come back through the same redirect on every OAuth path
 * (and on email verification), so the client cannot tell them apart on its
 * own. The worker marks the redirect that created the account with
 * `is_new_user=1`; this turns that marker plus the provider breadcrumb into
 * the one event to fire.
 *
 * Mirrors Swift's `Telemetry.trackAuthCompletion`. Kept pure and separate from
 * the store so the decision is testable without a DOM.
 */

export interface AuthArrival {
  /** Provider breadcrumb left before an OAuth round-trip, if this was one. */
  provider: string | null
  /** True when the worker says this exchange created the account. */
  isNewUser: boolean
  /** `signup_method` off the redirect. Authoritative when present. */
  signUpMethod?: string | null
}

export type AuthArrivalEvent =
  | { event: 'sign_up_completed'; method: string }
  | { event: 'user_logged_in'; provider: string }
  | null

/**
 * Exclusive by design: exactly one of the two fires, or none. A plain arrival
 * with no marker at all (a session restore on app open) reports nothing.
 *
 * The method comes from the redirect when the worker named it, and falls back
 * to the provider breadcrumb (then to email, the only other redirect that
 * creates an account) for a client that outlives a worker without it.
 */
export function authArrivalEvent(arrival: AuthArrival): AuthArrivalEvent {
  if (arrival.isNewUser) {
    return {
      event: 'sign_up_completed',
      method: arrival.signUpMethod ?? arrival.provider ?? 'email',
    }
  }
  if (arrival.provider) {
    return { event: 'user_logged_in', provider: arrival.provider }
  }
  return null
}
